/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { UserTypes } from '@constants/role';
import { AUTH } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { trackPageView } from '@core/analytics/mixpanel';
import { PageNames } from '@core/analytics/mixpanel/events';
import { useLazyGetAdminRolesAndPermissionsQuery } from '@features/admin/queries/AdminQuery';
import {
  useGetUserDetailsQuery,
  useLazyGetRolesAndPermissionsQuery
} from '@features/authentication/queries/AuthQuery';
import useCheckPermission from '@hooks/checkPermission';
import {
  setCurrentUser,
  setRolesAndPermissions
} from '@layouts/main-header/userSlice';
import { getLocalStorageItem } from '@utils/storage';

const AccessGuard = ({ children }: any) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { checkPageAccess } = useCheckPermission();

  const orgId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const token = getLocalStorageItem(STORAGE_KEYS.ACCESS_TOKEN);
  const userId = getLocalStorageItem(STORAGE_KEYS.USER_ID);
  const userType = getLocalStorageItem(STORAGE_KEYS.USER_TYPE);

  const [
    getRolesAndPermissions,
    {
      data: rolesAndPermissions,
      isFetching: isPermissionsFetching,
      isSuccess: isPermissionsSuccess
    }
  ] = useLazyGetRolesAndPermissionsQuery();

  const [
    getAdminRolesAndPermissions,
    {
      data: adminRolesAndPermissions,
      isFetching: isAdminPermissionsFetching,
      isSuccess: isAdminPermissionsSuccess
    }
  ] = useLazyGetAdminRolesAndPermissionsQuery();

  // Skip all queries if on login page and no token
  // (early return for better performance)
  const isLoginPage = pathname === AUTH.LOGIN;
  const shouldSkipQueries = isLoginPage && !token;

  const {
    data: currentUser,
    isFetching: isCurrentUserFetching,
    isSuccess: isCurrentUserSuccess
  } = useGetUserDetailsQuery(
    {
      userId,
      query: 'include_payment_stats=true&include_group_stats=true'
    },
    { skip: !userId || shouldSkipQueries, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!isCurrentUserFetching && isCurrentUserSuccess)
      dispatch(setCurrentUser({ user: currentUser }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (
      (!isPermissionsFetching && isPermissionsSuccess) ||
      (!isAdminPermissionsFetching && isAdminPermissionsSuccess)
    ) {
      dispatch(
        setRolesAndPermissions({
          permissions:
            userType === UserTypes.ADMIN
              ? adminRolesAndPermissions
              : rolesAndPermissions
        })
      );
    }
  }, [
    rolesAndPermissions,
    adminRolesAndPermissions,
    isAdminPermissionsFetching,
    isPermissionsFetching
  ]);

  useEffect(() => {
    const accessControlInfo =
      userType === UserTypes.ADMIN
        ? adminRolesAndPermissions
        : rolesAndPermissions;
    if (!token || !accessControlInfo) return;
    checkPageAccess({ accessControlInfo });
  }, [pathname, rolesAndPermissions, adminRolesAndPermissions]);

  useEffect(() => {
    if (!token || shouldSkipQueries) return;
    if (userType === UserTypes.ADMIN) getAdminRolesAndPermissions(null);
    else getRolesAndPermissions(orgId);
  }, [orgId, token, shouldSkipQueries]);

  useEffect(() => {
    // If page name is present in PageNames object, track the page view,
    // other pages will be tracked in their respective pages
    if (PageNames[pathname]) {
      trackPageView(PageNames[pathname]);
    }
  }, [pathname]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasUrlParams = searchParams.toString().length > 0;

    if (hasUrlParams) {
      return;
    }

    if (!token) {
      navigate(AUTH.LOGIN);
    }
  }, [token, navigate]);

  return children;
};

export default AccessGuard;
